import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon, CameraIcon } from '@heroicons/react/24/outline';
import ProfileSidebar from '../components/ProfileSidebar';
import TeamAlbum from '../components/TeamAlbum';
import RichTextEditor from '../components/RichTextEditor';

const Profile: React.FC = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    department: '',
    bio: '',
    email: '',
    phone: '',
    memberType: '',
    enrollmentYear: '',
    avatar: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const titles = [
    '研究员',
    '副研究员', 
    '教授',
    '副教授',
    '讲师',
    '博士后',
    '博士生',
    '硕士生',
    '本科生',
    '访问学者'
  ];

  const facultyTitles = ['研究员', '副研究员', '教授', '副教授', '讲师'];
  const studentTitles = ['博士后', '博士生', '硕士生', '本科生'];
  const otherTitles = ['访问学者'];

  // 根据注册类型获取可选的职称选项
  const getAvailableTitles = (memberType: string) => {
    switch (memberType) {
      case '组内教师':
        return facultyTitles;
      case '组内成员（在读学生）':
      case '组内成员（已毕业）':
        return studentTitles;
      case '组外成员':
        return otherTitles;
      default:
        return titles;
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.profile.name || '',
        title: user.profile.title || '',
        department: user.profile.department || '',
        bio: user.profile.bio || '',
        email: user.email || '',
        phone: user.profile.phone || '',
        memberType: user.profile.memberType || '组内成员',
        enrollmentYear: user.profile.enrollmentYear || '',
        avatar: user.profile.avatar || ''
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          avatar: e.target?.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 获取token
      const token = localStorage.getItem('token');
      const response = await fetch('https://ynuzhangwenhua.cn/api/auth/me', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ profile: formData })
      });
      const result = await response.json();
      if (result.success) {
        setSuccess('个人信息更新成功！');
        setIsEditing(false);
        // 更新本地用户信息
        if (result.data) {
          // 这里可以更新AuthContext中的用户信息
          window.location.reload(); // 简单的方式：刷新页面获取最新数据
        }
      } else {
        setError(result.message || '更新失败，请稍后重试');
      }
    } catch (err) {
      setError('更新失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const currentPassword = formData.get('currentPassword') as string;
    const newPassword = formData.get('newPassword') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('新密码长度至少6位');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://ynuzhangwenhua.cn/api/auth/change-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      
      const result = await response.json();
      if (result.success) {
        setSuccess('密码修改成功！');
        form.reset();
      } else {
        setError(result.message || '密码修改失败，请检查当前密码是否正确');
      }
    } catch (err) {
      setError('密码修改失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">请先登录</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">个人中心</h1>
        <p className="text-gray-600 mt-2">管理您的个人信息和账户设置</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* 左侧：导航栏 */}
        <div className="lg:col-span-1">
          <ProfileSidebar 
            activeTab={activeTab} 
            onTabChange={setActiveTab} 
          />
        </div>

        {/* 右侧：内容区域 */}
        <div className="lg:col-span-3 space-y-6">
          {/* 基本信息标签页 */}
          {activeTab === 'profile' && (
            <>
              {/* 头像和基本信息 */}
              <div className="card">
                <div className="text-center">
                  <div className="relative inline-block">
                    <img
                      src={formData.avatar || '/default-avatar.png'}
                      alt="头像"
                      className="w-32 h-32 rounded-full object-cover border-4 border-primary-100"
                    />
                    {isEditing && (
                      <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700">
                        <CameraIcon className="h-4 w-4" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 mt-4">{user.profile.name}</h2>
                  <p className="text-gray-600">{user.profile.title}</p>
                  <p className="text-sm text-gray-500 mt-1">{user.profile.department}</p>
                </div>
              </div>

              {/* 错误和成功消息 */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
                  {success}
                </div>
              )}

          {/* 基本信息 */}
          <div className="card">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">基本信息</h3>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                {isEditing ? '取消编辑' : '编辑信息'}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 profile-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    姓名
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    职称
                  </label>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                    required
                  >
                    <option value="">请选择职称</option>
                    {getAvailableTitles(formData.memberType).map(title => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮箱
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    手机号码
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    所属部门
                  </label>
                  <input
                    type="text"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    注册类型
                  </label>
                  <select
                    name="memberType"
                    value={formData.memberType}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                    required
                  >
                    <option value="组内成员（在读学生）">组内成员（在读学生）</option>
                    <option value="组内成员（已毕业）">组内成员（已毕业）</option>
                    <option value="组内教师">组内教师</option>
                    <option value="组外成员">组外成员</option>
                  </select>
                </div>

                {studentTitles.includes(formData.title) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      入学年份
                    </label>
                    <input
                      type="number"
                      name="enrollmentYear"
                      value={formData.enrollmentYear}
                      onChange={handleChange}
                      disabled={!isEditing}
                      min="2000"
                      max="2030"
                      className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="如：2024"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  个人简介
                </label>
                <RichTextEditor
                  value={formData.bio}
                  onChange={(value) => setFormData({ ...formData, bio: value })}
                  placeholder="请简要介绍自己..."
                  disabled={!isEditing}
                  height={150}
                />
              </div>

              {isEditing && (
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="btn-secondary"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? '保存中...' : '保存更改'}
                  </button>
                </div>
              )}
            </form>
          </div>


            </>
          )}

          {/* 账户安全标签页 */}
          {activeTab === 'security' && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">账户安全</h3>
              <div className="space-y-6">
                {/* 修改密码部分 */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">修改密码</h4>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        当前密码
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="currentPassword"
                          required
                          className="input-field pr-10"
                          placeholder="请输入当前密码"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        新密码
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          name="newPassword"
                          required
                          className="input-field pr-10"
                          placeholder="请输入新密码（至少6位）"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        确认新密码
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        required
                        className="input-field"
                        placeholder="请再次输入新密码"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary"
                      >
                        {loading ? '修改中...' : '修改密码'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* 登录历史 */}
                <div className="pt-6 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-4">登录历史</h4>
                  <div className="text-sm text-gray-600">
                    暂无登录历史记录
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 团队活动相册标签页 */}
          {activeTab === 'album' && (
            <div className="card">
              <TeamAlbum />
            </div>
          )}

          {/* 系统设置标签页 */}
          {activeTab === 'settings' && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">系统设置</h3>
              <div className="space-y-6">
                {/* 修改密码部分 */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">修改密码</h4>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        当前密码
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="currentPassword"
                          required
                          className="input-field pr-10"
                          placeholder="请输入当前密码"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        新密码
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          name="newPassword"
                          required
                          className="input-field pr-10"
                          placeholder="请输入新密码（至少6位）"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        确认新密码
                      </label>
                      <input
                        type="password"
                        name="confirmPassword"
                        required
                        className="input-field"
                        placeholder="请再次输入新密码"
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary"
                      >
                        {loading ? '修改中...' : '修改密码'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* 其他设置 */}
                <div className="pt-6 border-t border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-4">其他设置</h4>
                  <div className="text-sm text-gray-600">
                    暂无其他设置选项
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile; 